import React, { useCallback, useRef, ReactElement } from 'react';
import { isAndroid } from 'react-device-detect';

import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { FeedbackCommentType } from 'Shared/constants/types';

import SidePanelTextEditorGifPicker from '../SidePanelTextEditorGifPicker/SidePanelTextEditorGifPicker';
import SidePanelTextEditorEmojiPicker from '../SidePanelTextEditorEmojiPicker/SidePanelTextEditorEmojiPicker';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

import './SidePanelTextEditorToolbar.scss';

type Props = {
  showSaveButton?: boolean;
  isSaving?: boolean;
  hideVideo: boolean;
  commentForUpdate?: FeedbackCommentType;
  attachmentInputEl: React.Ref<HTMLInputElement>;
  onGifAdd: Function;
  onEmojiAdd: Function;
  onAttachmentIconClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onVideoRecorderToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onAttachmentInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCommentCreateClick: () => void;
  hint?: React.ReactNode;
};

function SidePanelTextEditorToolbar({
  showSaveButton = false,
  hideVideo,
  isSaving,
  commentForUpdate,
  attachmentInputEl,
  onGifAdd,
  onEmojiAdd,
  onAttachmentIconClick,
  onVideoRecorderToggle,
  onAttachmentInputChange,
  onCommentCreateClick,
  hint,
}: Props): ReactElement {
  const androidCommentSendBtnRef = useRef(null);

  const handleCreateCommentClick = useCallback(() => {
    if (!isAndroid) onCommentCreateClick();
    else setTimeout(() => androidCommentSendBtnRef.current?.click(), 100);
  }, [isAndroid, onCommentCreateClick, androidCommentSendBtnRef.current]);

  return (
    <div className="SidePanelTextEditorToolbar">
      <div>{hint}</div>
      <div className="SidePanelTextEditorToolbar__options">
        <SidePanelTextEditorGifPicker onGifAdd={onGifAdd} />
        <SidePanelTextEditorEmojiPicker onEmojiAdd={onEmojiAdd} />
        <WpTooltip placement="top">
          <WpTooltipTrigger>
            <WpButton variant="tertiary" onClick={onAttachmentIconClick} size="sm">
              <WpIcon name="paperclip" />
            </WpButton>
          </WpTooltipTrigger>
          <WpTooltipContent>Attach file</WpTooltipContent>
        </WpTooltip>
        {!hideVideo && (
          <WpTooltip placement="top">
            <WpTooltipTrigger>
              <WpButton variant="tertiary" onClick={onVideoRecorderToggle} size="sm">
                <WpIcon name="video" />
              </WpButton>
            </WpTooltipTrigger>
            <WpTooltipContent>Attach video</WpTooltipContent>
          </WpTooltip>
        )}
        <input
          type="file"
          className="SidePanelTextEditorToolbar__attachment-input"
          ref={attachmentInputEl}
          value=""
          onChange={onAttachmentInputChange}
        />

        {!commentForUpdate && !showSaveButton && (
          <WpTooltip placement="top">
            <WpTooltipTrigger>
              <WpButton variant="tertiary" onClick={handleCreateCommentClick} size="sm">
                <WpIcon name="paper-plane" />
              </WpButton>
            </WpTooltipTrigger>
            <WpTooltipContent>Send</WpTooltipContent>
          </WpTooltip>
        )}

        {isAndroid && <button ref={androidCommentSendBtnRef} className="d-none" onClick={onCommentCreateClick} />}

        {showSaveButton && (
          <div className="ml-2">
            <WpButton onClick={handleCreateCommentClick} variant="secondary" loading={isSaving}>
              Save
            </WpButton>
          </div>
        )}
      </div>
    </div>
  );
}

export default SidePanelTextEditorToolbar;
